import { idb } from "../db/index.ts";
import { g, helpers, updatePlayMenu, updateStatus } from "../util/index.ts";
import getPlayoffsByConf from "../core/season/getPlayoffsByConf.ts";
import { getRoundsWonText } from "./teamSeasonsShared.ts";

// M3-5: the standalone inbox page (view "inbox", route /l/:lid/inbox) and the
// message page (view "message", routes /l/:lid/message[/:mid]) were retired into
// the two-level InboxSheet overlay opened in place from the league home surface
// (see MOBILE_SHEET_CONTRACT_V1 in app/src/ui/components/MobileSheet.tsx). These
// two worker RPCs replace the old page-view pipeline, fetched ad hoc on sheet
// open (loading spinner first) — the M3 data pattern:
//
//   getInboxSheet()        -> { anyUnread, messages[] }   (the list level)
//   getMessageSheet(mid)   -> { message }                 (the letter level; this
//                             is where the mark-as-read side effects fire, exactly
//                             as the old message page-view did)
//
// The return shapes are anonymous structural types (no exported alias) so the RPC
// codegen (tools/genApiTypes.ts) expands them inline in
// common/types/.gen/workerApi.d.ts rather than emitting an unresolved name.

// getInboxSheet reproduces the old server/worker/views/inbox.ts: newest message
// first, <p> tags flattened out of the body (the queue rows render plain text),
// and the anyUnread flag the "new message" banner keys off. Mapped to a compact
// row (the fields the queue classifier + rows actually read) so codegen infers a
// concrete row type instead of the full Message record.
export const getInboxSheet = async () => {
	const messagesRaw = await idb.getCopies.messages();
	messagesRaw.reverse();

	let anyUnread = false;
	const messages = messagesRaw.map((message) => {
		if (!message.read) {
			anyUnread = true;
		}

		return {
			mid: message.mid,
			from: message.from,
			read: message.read,
			subject: message.subject,
			// Needs to strip the block tags so the flattened body reads as one line
			// in the queue row (matches the old inbox view's replaceAll pass).
			text: message.text.replaceAll("<p>", "").replaceAll("</p>", " "),
			year: message.year,
		};
	});

	return {
		anyUnread,
		messages,
	};
};

// getMessageSheet reproduces the old server/worker/views/message.ts letter view:
// it marks the message read (the CORE side effect — flips read, persists it, and
// refreshes the play menu / fired status exactly as the page did), then augments
// the owner-mood history with per-season record/profit context for the trust
// chart. The old no-mid auto-redirect entry is gone (the play menu now lands on
// the home surface with ?m_open_latest=1, which drills the sheet to the latest
// pending letter); this endpoint always takes a concrete mid.
export const getMessageSheet = async (mid: number) => {
	const message = await idb.getCopy.messages({ mid }, "noCopyCache");

	// Mark-as-read side effect — fires once, only when the letter was unread, just
	// as the old page-view did on first render of an unread message.
	if (message && !message.read) {
		message.read = true;
		await idb.cache.messages.put(message);

		if (g.get("gameOver")) {
			await updateStatus("You're fired!");
		}

		await updatePlayMenu();
	}

	let augmentedMessage:
		| {
				mid: number;
				from: string;
				subject: string | undefined;
				text: string;
				year: number;
				tid: number | undefined;
				read: boolean;
				ownerMoods:
					| {
							money: number;
							playoffs: number;
							wins: number;
							total: number;
							season: number;
							seasonInfo:
								| {
										won: number;
										lost: number;
										tied: number;
										otl: number;
										champ: boolean;
										profit: number;
										roundsWonText: string;
								  }
								| undefined;
					  }[]
				| undefined;
		  }
		| undefined;

	if (message) {
		let augmentedOwnerMoods;
		if (message.ownerMoods) {
			augmentedOwnerMoods = [];

			for (let i = 0; i < message.ownerMoods.length; i++) {
				const mood = message.ownerMoods[i]!;

				const season = message.year - message.ownerMoods.length + 1 + i;

				const teamSeason = await idb.getCopy.teamSeasons({
					// Old messages don't include tid
					tid: message.tid ?? g.get("userTid"),
					season,
				});

				let seasonInfo;
				if (teamSeason) {
					const revenue = helpers
						.keys(teamSeason.revenues)
						.reduce((memo, rev) => memo + teamSeason.revenues[rev], 0);
					const expense = helpers
						.keys(teamSeason.expenses)
						.reduce((memo, rev) => memo + teamSeason.expenses[rev], 0);
					const profit = (revenue - expense) / 1000; // [millions of dollars]

					const roundsWonText = getRoundsWonText(
						teamSeason,
						await getPlayoffsByConf(teamSeason.season),
					);

					seasonInfo = {
						won: teamSeason.won,
						lost: teamSeason.lost,
						tied: teamSeason.tied,
						otl: teamSeason.otl,
						champ:
							teamSeason.playoffRoundsWon ===
							g.get("numGamesPlayoffSeries", teamSeason.season).length,
						profit,
						roundsWonText,
					};
				}

				augmentedOwnerMoods.push({
					money: mood.money,
					playoffs: mood.playoffs,
					wins: mood.wins,
					total: mood.money + mood.playoffs + mood.wins,
					season,
					seasonInfo,
				});
			}
		}

		augmentedMessage = {
			mid: message.mid,
			from: message.from,
			subject: message.subject,
			text: message.text,
			year: message.year,
			tid: message.tid,
			read: message.read,
			ownerMoods: augmentedOwnerMoods,
		};
	}

	return {
		message: augmentedMessage,
	};
};
